import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://backend-production-9457.up.railway.app';

const PLANS = [
  { id:'free',    label:'Free',    price:'$0',   period:'/mo', sessions:2,   seats:1,  features:['2 sessions/month','1 user','MCP server access','Basic synthesis'] },
  { id:'starter', label:'Starter', price:'$29',  period:'/mo', sessions:15,  seats:1,  features:['15 sessions/month','1 user','Priority processing','PDF & Markdown reports','Email support'] },
  { id:'growth',  label:'Growth',  price:'$89',  period:'/mo', sessions:50,  seats:3,  features:['50 sessions/month','3 seats included','Team collaboration','Semantic search','Slack community','+$20/extra seat/mo'] },
  { id:'team',    label:'Team',    price:'$249', period:'/mo', sessions:150, seats:10, features:['150 sessions/month','10 seats included','Custom integrations','Dedicated support','SLA available','+$15/extra seat/mo'] },
];

export default function Billing() {
  const { getToken } = useAuth();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [upgradeError, setUpgradeError] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/portal/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) setAccount(await res.json());
      } finally { setLoading(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleUpgrade(planId) {
    setUpgrading(planId); setUpgradeError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/billing/create-checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ plan: planId }),
      });
      if (res.ok) { const d = await res.json(); window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: 'plan_upgrade', plan: planId }); window.location.href = d.url; }
      else { const d = await res.json(); setUpgradeError(d.detail || 'Could not start checkout. Please try again.'); }
    } catch { setUpgradeError('Could not reach the server. Please try again.'); }
    finally { setUpgrading(null); }
  }

  async function handleManageSubscription() {
    setPortalLoading(true); setUpgradeError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/billing/portal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ stripe_customer_id: account?.stripe_customer_id }),
      });
      if (res.ok) { const d = await res.json(); window.open(d.portal_url || d.url, '_blank'); }
      else { const d = await res.json(); setUpgradeError(d.detail || 'Could not open billing portal. Please try again.'); }
    } catch { setUpgradeError('Could not reach the server. Please try again.'); }
    finally { setPortalLoading(false); }
  }

  if (loading) return <div style={s.center}>Loading billing info...</div>;

  const currentPlan = account?.plan || 'free';

  return (
    <div>
      <div style={s.header}>
        <h1 style={s.h1}>Billing</h1>
        <p style={s.subtitle}>Manage your plan and subscription.</p>
      </div>

      {upgradeError && <div style={s.errorBanner}>{upgradeError}</div>}

      {/* Current plan banner */}
      <div className="scq-card scq-current-banner" style={s.currentBanner}>
        <div>
          <div style={s.currentLabel}>Current plan</div>
          <div style={s.currentPlan}>{currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}</div>
        </div>
        <div style={s.currentRight}>
          <span style={s.currentSessions}>{account?.sessions_this_month || 0} sessions used this month</span>
          {account?.stripe_customer_id && (
            <button onClick={handleManageSubscription} disabled={portalLoading} style={s.portalLinkBtn}>
              {portalLoading ? 'Loading…' : 'Manage subscription →'}
            </button>
          )}
        </div>
      </div>

      {/* Plan grid */}
      <div style={s.planGrid} className="scq-grid-4">
        {PLANS.map(plan => {
          const isCurrent = plan.id === currentPlan;
          const isPopular = plan.id === 'growth';
          return (
            <div key={plan.id} className="scq-card" style={{...s.planCard, ...(isCurrent ? s.planCardActive : {}), ...(isPopular ? s.planCardPopular : {})}}>
              {isPopular && <div style={s.popularTag}>Most popular</div>}
              <div style={s.planLabel}>{plan.label}</div>
              <div style={s.planPrice}>
                {plan.price}<span style={s.planPeriod}>{plan.period}</span>
              </div>
              <div style={s.planSessions}>{plan.sessions} sessions · {plan.seats === 1 ? '1 user' : `${plan.seats} seats`}</div>
              <ul style={s.featureList}>
                {plan.features.map(f => (
                  <li key={f} style={s.featureItem}>
                    <span style={s.featureCheck}>✓</span> {f}
                  </li>
                ))}
              </ul>
              <button
                style={{...s.planBtn, ...(isCurrent ? s.planBtnCurrent : {})}}
                onClick={() => !isCurrent && handleUpgrade(plan.id)}
                disabled={isCurrent || upgrading === plan.id}
              >
                {isCurrent ? 'Current plan' : upgrading === plan.id ? 'Redirecting…' : plan.id === 'free' ? 'Downgrade' : 'Upgrade'}
              </button>
            </div>
          );
        })}
      </div>

      <p style={s.note}>Payments processed securely by Stripe. Cancel anytime.</p>
    </div>
  );
}

const s = {
  center: { color:'var(--color-muted)', textAlign:'center', padding:'80px 0', fontSize:14 },
  errorBanner: { background:'rgba(229,62,62,0.10)', border:'1px solid rgba(229,62,62,0.25)', borderRadius:'var(--radius-md)', padding:'12px 16px', color:'var(--color-danger)', fontSize:13, marginBottom:20 },
  header: { marginBottom:28 },
  h1: { fontFamily:'var(--font-display)', fontSize:28, fontWeight:700, letterSpacing:'-0.02em', color:'var(--color-text)', margin:0 },
  subtitle: { color:'var(--color-muted)', marginTop:6, fontSize:14 },
  currentBanner: { background:'var(--color-card)', border:'1px solid var(--color-border2)', borderRadius:'var(--radius-lg)', padding:'20px 24px', marginBottom:28, display:'flex', justifyContent:'space-between', alignItems:'center', boxShadow:'var(--shadow-card)' },
  currentLabel: { fontSize:11, fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--color-accent)', marginBottom:4 },
  currentPlan: { fontFamily:'var(--font-display)', fontSize:22, fontWeight:700, color:'var(--color-text)' },
  currentRight: { display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 },
  currentSessions: { fontSize:13, color:'var(--color-muted)' },
  portalLink: { fontSize:13, color:'var(--color-accent)', textDecoration:'none', fontWeight:500 },
  portalLinkBtn: { fontSize:13, color:'var(--color-accent)', fontWeight:500, background:'none', border:'none', cursor:'pointer', padding:0 },
  planGrid: { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:20 },
  planCard: { background:'var(--color-card)', border:'1px solid var(--color-border)', borderRadius:'var(--radius-xl)', padding:24, position:'relative', display:'flex', flexDirection:'column', boxShadow:'var(--shadow-card)' },
  planCardActive: { border:'1px solid var(--color-accent-border)', background:'var(--color-card2)' },
  planCardPopular: { border:'1px solid rgba(255,87,34,0.3)' },
  popularTag: { position:'absolute', top:-1, right:16, fontSize:10, fontWeight:700, fontFamily:'var(--font-display)', letterSpacing:'0.07em', textTransform:'uppercase', color:'#fff', background:'var(--color-magma)', padding:'4px 10px', borderRadius:'0 0 8px 8px' },
  planLabel: { fontFamily:'var(--font-display)', fontSize:13, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', color:'var(--color-muted)', marginBottom:12 },
  planPrice: { fontFamily:'var(--font-display)', fontSize:38, fontWeight:700, letterSpacing:'-0.04em', color:'var(--color-text)', marginBottom:2 },
  planPeriod: { fontSize:14, fontWeight:400, color:'var(--color-muted)' },
  planSessions: { fontSize:13, color:'var(--color-accent)', marginBottom:20, fontWeight:500 },
  featureList: { listStyle:'none', padding:0, margin:'0 0 24px', flex:1 },
  featureItem: { fontSize:13, color:'var(--color-muted)', marginBottom:9, display:'flex', gap:8 },
  featureCheck: { color:'var(--color-green)', flexShrink:0 },
  planBtn: { width:'100%', padding:'11px 0', borderRadius:'var(--radius-lg)', fontFamily:'var(--font-display)', fontWeight:700, fontSize:14, background:'var(--color-magma)', color:'#fff', border:'none', cursor:'pointer', marginTop:'auto' },
  planBtnCurrent: { background:'var(--color-accent-dim)', color:'var(--color-accent)', border:'1px solid var(--color-accent-border)', cursor:'default' },
  note: { fontSize:12, color:'var(--color-muted2)', textAlign:'center' },
};
